import { useState, useCallback } from 'react';
import { useEntitlement } from '../hooks/useEntitlement';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';
import { getCampaignPromo } from '../lib/campaignParams';

function todayDismissKey(): string {
  return `hbs:free-banner-dismissed-${new Date().toISOString().slice(0, 10)}`;
}

/**
 * Persistent top-of-page banner shown whenever the user is in
 * `state === 'free'` (never subscribed / trial lapsed) and hasn't dismissed
 * today's banner. It nudges toward a subscription with the two things a free
 * account can't do: create/join Families, and use the app on more than one
 * device at a time.
 *
 * Modeled on TrialBanner: the Subscribe button mints a one-time handoff token
 * (same flow as Settings → Manage subscription) and navigates the current tab
 * so "Return to safe" lands back here. Dismissal lasts one calendar day — the
 * next day's banner has its own sessionStorage key so the nudge returns.
 *
 * The transient "subscription state changed" banner is suppressed for free
 * users (see TransientBanner), so this is the single free-state message.
 */
export function FreeAccountBanner() {
  const { state, activePromoCode } = useEntitlement();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(todayDismissKey()) === '1';
    } catch {
      return false;
    }
  });
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDismiss = useCallback(() => {
    try { sessionStorage.setItem(todayDismissKey(), '1'); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  const handleSubscribe = useCallback(async () => {
    setError(null);
    setOpening(true);
    try {
      // A campaign code beats the global one — see the same change in
      // TrialBanner.tsx for why.
      const url = await createSubscribeHandoffUrl({
        promoCode: getCampaignPromo() ?? activePromoCode?.code,
      });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpening(false);
    }
  }, [activePromoCode]);

  if (state !== 'free') return null;
  if (dismissed) return null;

  return (
    <div className="bg-sky-100 border-b border-sky-400 px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-sky-950">
        You&apos;re on a free account.{' '}
        <button
          id="free-account-subscribe-button"
          type="button"
          data-testid="subscribe-cta"
          onClick={handleSubscribe}
          disabled={opening}
          className="text-sky-950 underline font-medium hover:text-sky-700 disabled:opacity-60"
        >
          {opening ? 'Opening…' : 'Subscribe'}
        </button>{' '}
        to unlock Families and use Hey Be Safe on all your devices.
        {error && <span className="ml-2 text-red-700">{error}</span>}
      </span>
      <button
        id="free-account-banner-dismiss-button"
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="text-sky-950 hover:text-sky-700 text-sm"
      >
        ×
      </button>
    </div>
  );
}
